// Story 1.20 — Article 20 portability section on the GdprPage.
//
// Mirror of DataExportSection scoped to the portability surface. The
// only visible difference is the explanatory copy + the schema doc link;
// the lifecycle + status display logic is identical.

import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@greenhold/components";
import toast from "react-hot-toast";

import { useDsarStatus } from "./useDsarStatus";
import { useExportRequest } from "./useExportRequest";
import { useRequestPortabilityExport } from "./useRequestPortabilityExport";

const SCHEMA_DOC_URL = "https://greenhold.world/docs/portability-schema";

export function PortabilityExportSection() {
  const dsar = useDsarStatus();
  const activeId = dsar.data?.data.portability.active_request_id ?? null;
  const exportReq = useExportRequest(activeId);
  const mutate = useRequestPortabilityExport();

  const handleRequest = () => {
    mutate.mutate(undefined, {
      onSuccess: () => toast.success("Portability bundle requested."),
      onError: (e) => toast.error(e.message),
    });
  };

  const current = exportReq.data?.data;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Portability bundle (Article 20)</CardTitle>
        <CardDescription>
          A JSON-LD bundle suitable for migrating your account data to
          another platform. The schema is documented at{" "}
          <a href={SCHEMA_DOC_URL} className="underline">
            {SCHEMA_DOC_URL}
          </a>
          {" "}so any receiving platform can read it without Greenhold-specific
          code.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!activeId && !current && (
          <Button
            variant="primary"
            onClick={handleRequest}
            disabled={mutate.isPending}
          >
            {mutate.isPending ? "Requesting…" : "Request portability bundle"}
          </Button>
        )}

        {current?.status === "pending" || current?.status === "processing" ? (
          <p className="text-sm opacity-80">
            Your bundle is being compiled (status: <strong>{current.status}</strong>).
            This page checks for updates every 10 seconds.
          </p>
        ) : null}

        {current?.status === "completed" && current.download_url ? (
          <div className="space-y-2">
            <p className="text-sm">
              Bundle ready ({formatBytes(current.bundle_size_bytes ?? 0)}).
            </p>
            <Button variant="primary" asChild>
              <a href={current.download_url} download>
                Download portability bundle
              </a>
            </Button>
            {current.download_url_expires_at && (
              <p className="text-xs opacity-70">
                Link expires {new Date(current.download_url_expires_at).toLocaleString()}.
              </p>
            )}
          </div>
        ) : null}

        {current?.status === "failed" && (
          <div className="space-y-2">
            <p className="text-sm text-red-700">
              Compilation failed: {current.failure_message || "unknown error"}.
            </p>
            <Button variant="secondary" onClick={handleRequest}>
              Try again
            </Button>
          </div>
        )}

        {current?.status === "expired" && (
          <div className="space-y-2">
            <p className="text-sm">
              Download link expired. Request a fresh bundle.
            </p>
            <Button variant="primary" onClick={handleRequest}>
              Re-request
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}
